/**
 * FeaturedCarousel — auto-rotating strip of Editors' Picks.
 *
 * Sits between the Hero and Explore Your World sections on BlogHome
 * so first-time visitors instantly see standout stories. Rotates
 * every 6s with pause-on-hover, arrow controls, and dot indicators.
 *
 * Prop `picks` is the shape returned by
 *   GET /api/publisher/v1/blog/homepage → editors_picks[]
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight, Sparkles } from "lucide-react";

const ROTATE_MS = 6000;

export default function FeaturedCarousel({ picks = [] }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const total = picks.length;

  useEffect(() => {
    if (paused || total <= 1) return undefined;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % total);
    }, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, total]);

  if (!picks || total === 0) return null;

  const current = picks[idx];
  const goto = (n) => setIdx(((n % total) + total) % total);
  const prev = () => goto(idx - 1);
  const next = () => goto(idx + 1);

  return (
    <section
      className="pb-10 md:pb-14"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      aria-label="Editors' Picks"
      data-testid="featured-carousel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold rounded-full px-3 py-1"
            style={{ background: "rgba(15,118,110,0.09)", color: "#0F766E" }}
          >
            <Sparkles className="w-3 h-3" /> Editors' Picks
          </span>
          <span className="text-xs text-slate-500 hidden sm:inline">
            Handpicked from across the network
          </span>
        </div>
        {total > 1 && (
          <div className="flex items-center gap-1.5" data-testid="featured-carousel-controls">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous story"
              className="w-8 h-8 rounded-full grid place-items-center bg-white border hover:border-teal-600 transition-colors"
              style={{ borderColor: "rgba(15,23,42,0.10)" }}
              data-testid="featured-carousel-prev"
            >
              <ChevronLeft className="w-4 h-4 text-slate-700" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next story"
              className="w-8 h-8 rounded-full grid place-items-center bg-white border hover:border-teal-600 transition-colors"
              style={{ borderColor: "rgba(15,23,42,0.10)" }}
              data-testid="featured-carousel-next"
            >
              <ChevronRight className="w-4 h-4 text-slate-700" />
            </button>
          </div>
        )}
      </div>

      <Link
        to={current.href}
        className="group block rounded-3xl overflow-hidden bg-white border transition-shadow hover:shadow-lg"
        style={{ borderColor: "rgba(15,23,42,0.08)" }}
        data-testid={`featured-slide-${idx}`}
        aria-live="polite"
      >
        <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr] min-h-[280px] md:min-h-[360px]">
          {/* Cover */}
          <div className="relative overflow-hidden bg-slate-100">
            {current.cover ? (
              <img
                src={current.cover}
                alt=""
                className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                style={{ transitionDuration: "6000ms" }}
                loading="lazy"
              />
            ) : (
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(135deg,#0F766E,#134E4A)" }}
              />
            )}
            {current.category?.label && (
              <span
                className="absolute top-4 left-4 inline-block text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-white/95 backdrop-blur"
                style={{ color: current.category.color || "#0F766E" }}
              >
                {current.category.label}
              </span>
            )}
          </div>

          {/* Copy */}
          <div className="p-6 md:p-8 flex flex-col justify-center">
            {current.place && (
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">
                {current.place}
              </p>
            )}
            <h3
              className="text-slate-950 leading-tight"
              style={{
                fontFamily: "'Lora', serif",
                fontWeight: 600,
                fontSize: "clamp(1.35rem, 2.4vw, 2rem)",
                letterSpacing: "-0.015em",
              }}
              data-testid="featured-slide-title"
            >
              {current.title}
            </h3>
            {current.summary && (
              <p className="mt-3 text-slate-600 text-sm md:text-base leading-relaxed line-clamp-3">
                {current.summary}
              </p>
            )}
            <div className="mt-5 flex items-center justify-between gap-3">
              <span className="text-xs text-slate-500">
                By <span className="font-semibold text-slate-800">{current.author}</span>
              </span>
              <span
                className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest group-hover:underline"
                style={{ color: "#0F766E" }}
              >
                Read <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </div>
        </div>
      </Link>

      {/* Dot indicators */}
      {total > 1 && (
        <div
          className="mt-4 flex items-center justify-center gap-1.5"
          role="tablist"
          aria-label="Featured story indicators"
          data-testid="featured-carousel-dots"
        >
          {picks.map((_, i) => {
            const active = i === idx;
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={`Go to featured story ${i + 1}`}
                onClick={() => goto(i)}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: active ? 24 : 8,
                  background: active ? "#0F766E" : "rgba(15,23,42,0.18)",
                }}
                data-testid={`featured-carousel-dot-${i}`}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
